/**
 * Shared machinery for the `<resource>.payload.ts` mappers.
 *
 * Those mappers translate a nested READ model (the detail object the form binds, carrying
 * `country: {...}`) into the flat WRITE contract the backend accepts since acted-core#233
 * (`country_id: 7`). Each mapper declares its relation table and its writable-scalar list; the
 * emission rules, and the compile-time guarantee that no contract key is silently forgotten, live
 * here.
 *
 * Mirrored (not imported) from acted-front/src/app/components/generic/payload.ts - separate repos
 * cannot share a module. Keep the two in sync.
 */

/**
 * Map a relation on the form model to the scalar value the backend resolves the FK by.
 *
 * `undefined` (never set on the form model) yields `undefined`, and buildPayload drops the key, so
 *   the backend leaves the relation untouched - its write fields are all `required=False`.
 * `null` (cleared by the user in the form) yields `null`, the backend's explicit "clear" signal.
 * Never `''`: the backend's related fields reject the empty string.
 *
 * A present object whose lookup value is missing also yields `undefined` rather than `null`: that
 * only happens on a malformed object, and dropping the key preserves the stored relation instead of
 * silently deleting it.
 */
export function relationRef<T, V extends string | number> (
  relation: T | null | undefined,
  lookup: (relation: T) => V | undefined,
): V | null | undefined {
  if (relation === undefined) return undefined
  if (relation === null) return null
  return lookup(relation)
}

/**
 * One relation: the model property it is read from, the wire key it is written as, and how to
 * resolve the lookup value.
 *
 * Deliberately a DISTRIBUTIVE MAPPED UNION rather than the simpler
 * `{ key: keyof Payload, lookup: (model: Detail) => string | number | null | undefined }`: the union
 * ties each entry's `lookup` return type to its OWN `Payload[K]`, so a relation whose wire key
 * expects a number cannot be handed a string. The simpler signature type-checks that mistake
 * happily, which would put a 400 at runtime instead of an error at build time.
 *
 * `field` names the model property the relation is read from. It is what `buildPayload`'s `pick`
 * hook is keyed by, and it documents the nested-read / flat-write pairing for mappers that have no
 * `pick`.
 */
export type PayloadRelation<Detail, Payload> = {
  [K in keyof Payload]: {
    field: DeclaredKey<Detail>
    key: K
    lookup: (model: Detail) => Payload[K] | undefined
  }
}[keyof Payload]

/**
 * The keys a model DECLARES, dropping any index signature.
 *
 * Plain `keyof Detail` would be enough for a generated model, but a hand-written one may carry an
 * index signature for dynamic fields (`ArchiveDocumentForm` has `[fieldName: string]: unknown`), and
 * that makes `keyof Detail` swallow the literal keys into `string` - so every typo'd `field` would
 * type-check. Filtering the signature out keeps a misspelled relation field an error everywhere.
 */
export type DeclaredKey<T> = keyof {
  [K in keyof T as string extends K ? never : number extends K ? never : K]: T[K]
} & string

/**
 * A key present on both the read model and the write contract, whose read type is actually
 * assignable to the write type.
 *
 * Both sides are generated from the same serializer field, so a divergence should be impossible -
 * this exists so that if one ever appears, it is a build error rather than a value the backend
 * rejects.
 */
export type CompatibleScalarKey<Detail, Payload> = {
  [K in keyof Payload & keyof Detail]: Detail[K] extends Payload[K] ? K : never
}[keyof Payload & keyof Detail]

/**
 * Narrow a value to the subset a save should actually send.
 *
 * Used by the archives mapper, whose form shows only the active document type's fields: values
 * outside that set are cleared on an individual save and omitted in bulk. Mappers with no such
 * notion omit the hook entirely and every value passes through.
 */
export type PayloadPick = <T>(field: string, value: T) => T | null | undefined

const passThrough: PayloadPick = (_field, value) => value

/**
 * Build the flat write payload for `model`.
 *
 * Emission rule, uniform across relations and scalars: `undefined` drops the key (the backend leaves
 * that field untouched - PATCH semantics), anything else is sent as-is, `null` included (the
 * backend's explicit "clear" signal).
 *
 * Nothing reads the model except through `relations` and `scalars`, so read-only keys can never leak
 * onto the wire.
 */
export function buildPayload<Detail extends object, Payload extends object> (
  model: Detail,
  relations: readonly PayloadRelation<Detail, Payload>[],
  scalars: readonly CompatibleScalarKey<Detail, Payload>[],
  pick: PayloadPick = passThrough,
): Payload {
  const payload = {} as Payload
  // The one cast in this module. TypeScript cannot correlate `payload[key] = model[key]` while
  // Detail and Payload are still type parameters, so the per-key checking is done at the CALL site
  // instead - by PayloadRelation's mapped union and by CompatibleScalarKey - and erased here.
  const emit = (key: keyof Payload, value: unknown): void => {
    if (value !== undefined) (payload as Record<keyof Payload, unknown>)[key] = value
  }

  for (const relation of relations) {
    emit(relation.key, pick(relation.field, relation.lookup(model)))
  }

  for (const key of scalars) {
    // a scalar's model property and its wire key are the same string, so `key` is also its `field`
    emit(key, pick(key as string, model[key as unknown as keyof Detail]))
  }

  return payload
}

/**
 * Coverage guard. Instantiate per mapper with the contract keys it does NOT account for:
 *
 * ```ts
 * type UncoveredWriteKey = Exclude<keyof MyPayload, (typeof WRITABLE_SCALARS)[number] | RelationWireKey>
 * export type EveryWriteKeyIsCovered = NoUncoveredWriteKey<UncoveredWriteKey>
 * ```
 *
 * Compilation then fails unless every key of the generated request contract is either a
 * WRITABLE_SCALARS entry or a relation - so a field added backend-side cannot be silently dropped.
 *
 * Two things the instantiation must get right:
 * - anchor it to `keyof Payload`, not to `keyof Payload & keyof Detail`. A NEW RELATION's `<name>_id`
 *   key exists only on the request side, so an intersection-based guard never sees it.
 * - subtract what WRITABLE_SCALARS actually CONTAINS, not the key type it is constrained by. That
 *   type is derived from the contract and so can never lag behind it, which would make the guard
 *   blind to an entry missing from the list.
 *
 * Type-only on purpose - it carries no runtime value, so nothing is emitted into the bundle.
 */
export type NoUncoveredWriteKey<Uncovered extends never> = Uncovered
